import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useI18n } from "../i18n/I18nContext";
import { C } from "../constants/design";
import { MATCHES, HDR_OFFSET, LIVE_MS } from "../constants/matches";
import { DayLabel } from "./ui/atoms";
import { MatchCard } from "./MatchCard";

const koTime = (m: { koUTC: string }) => new Date(m.koUTC).getTime();

// Placeholder team names that should not be shown until real teams are known
const PLACEHOLDER_RE = /^[12][A-L]$|^[WL]\d+$|\//;
const isPlaceholder = (m: { t1: string; t2: string }) =>
  PLACEHOLDER_RE.test(m.t1) || PLACEHOLDER_RE.test(m.t2);

const todayKST = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Seoul" });

const tomKST = () =>
  new Date(Date.now() + 86400000).toLocaleDateString("en-CA", {
    timeZone: "Asia/Seoul",
  });

const isToday = (d: string) => d === todayKST();

const pillLabel = (d: string, t: (k: string) => string) =>
  d === todayKST()
    ? t("pronos.today")
    : d === tomKST()
    ? t("pronos.tomorrow")
    : new Date(d + "T12:00:00Z").toLocaleDateString("en-US", {
        timeZone: "Asia/Seoul",
        day: "numeric",
        month: "short",
      });

interface Match {
  id: string;
  grp: string;
  date: string;
  koUTC: string;
  t1: string;
  t2: string;
  p1: number;
  pX: number;
  p2: number;
  status?: string;
}

interface MatchResult {
  o: string;
  s?: string;
}

export function Pronos({
  me,
  bets,
  results,
  onBet,
  onSetResult,
  refScores,
  onRefScore,
  predScores,
  onPredScore,
  matches = MATCHES as Match[],
}: {
  me?: unknown;
  bets: Record<string, string>;
  results: Record<string, MatchResult>;
  onBet: (id: string, outcome: string, a: number, b: number) => void;
  onSetResult?: (id: string, outcome: string, score: string) => void;
  refScores?: Record<string, { a: string; b: string }>;
  onRefScore?: (id: string, field: string, value: string) => void;
  predScores?: Record<string, { a: string; b: string }>;
  onPredScore?: (id: string, field: string, value: string) => void;
  matches?: Match[];
}) {
  const { t } = useI18n();
  const today = todayKST();
  // A match is visible if its kickoff hasn't happened yet (by koUTC, not date),
  // and it has real team names (not knockout placeholders).
  const isVisible = (m: Match) =>
    !isPlaceholder(m) && (m.date >= today || new Date(m.koUTC).getTime() > Date.now());
  const dates = useMemo(
    () =>
      [...new Set(matches.filter(isVisible).map((m) => m.date))].sort(),
    [matches, today]
  );
  const byDate = useMemo(() => {
    const o: Record<string, Match[]> = {};
    matches.filter(isVisible).forEach((m) => {
      const ko = new Date(m.koUTC).getTime();
      const isBetClosed = Date.now() >= ko;
      console.log(
        `[match] id=${m.id} ${m.t1} vs ${m.t2} koUTC=${m.koUTC} KST=${new Date(m.koUTC).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })} closed=${isBetClosed}`
      );
      (o[m.date] = o[m.date] || []).push(m);
    });
    return o;
  }, [matches, today]);
  const initial = useMemo(
    () => (dates.includes(today) ? today : dates[0] || "matchs-termines"),
    [dates]
  );
  const [active, setActive] = useState(initial);

  useEffect(() => {
    if (matches.length === MATCHES.length) return;
    const t = todayKST();
    const now = Date.now();
    const fresh = [...new Set(
      matches
        .filter((m) => !isPlaceholder(m) && (m.date >= t || new Date(m.koUTC).getTime() > now))
        .map((m) => m.date)
    )].sort();
    const next = fresh.includes(t) ? t : fresh[0] || "matchs-termines";
    setActive(next);
  }, [matches]);

  const showFin = active === "matchs-termines";

  const finByDate = useMemo(() => {
    const o: Record<string, Match[]> = {};
    matches
      .filter((m) => !isPlaceholder(m) && results[m.id])
      .forEach((m) => {
        (o[m.date] = o[m.date] || []).push(m);
      });
    return o;
  }, [results, matches]);
  const finDates = useMemo(
    () => Object.keys(finByDate).sort().reverse(),
    [finByDate]
  );
  const totalFin = useMemo(
    () => matches.filter((m) => !isPlaceholder(m) && results[m.id]).length,
    [results, matches]
  );

  const sRefs = useRef<Record<string, HTMLElement | null>>({});
  const pRefs = useRef<Record<string, HTMLElement | null>>({});
  const scrolling = useRef(false);

  const scrollTo = useCallback((d: string) => {
    const el = sRefs.current[d];
    if (!el) return;
    scrolling.current = true;
    setActive(d);
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - HDR_OFFSET,
      behavior: "smooth",
    });
    setTimeout(() => {
      scrolling.current = false;
    }, 900);
  }, []);

  const goDate = useCallback(
    (d: string) => {
      setActive(d);
      setTimeout(() => scrollTo(d), 60);
    },
    [scrollTo]
  );

  useEffect(() => {
    if (showFin) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (scrolling.current) return;
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (vis[0]) setActive((vis[0].target as HTMLElement).dataset.date || "");
      },
      { rootMargin: `-${HDR_OFFSET}px 0px -55% 0px`, threshold: 0 }
    );
    Object.values(sRefs.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [showFin]);

  useEffect(() => {
    if (initial !== "matchs-termines") {
      const t = setTimeout(() => scrollTo(initial), 250);
      return () => clearTimeout(t);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const el = pRefs.current[active];
    if (el)
      el.scrollIntoView({
        inline: "center",
        block: "nearest",
        behavior: "smooth",
      });
  }, [active]);

  return (
    <div>
      <div
        style={{
          position: "sticky",
          top: 49,
          zIndex: 30,
          background: `${C.bg}EE`,
          backdropFilter: "blur(8px)",
          borderBottom: `1px solid ${C.lineSoft}`,
          padding: "8px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 14px 7px",
          }}
        >
          <div
            className="d"
            style={{
              fontWeight: 900,
              fontSize: 13,
              color: C.muted,
              letterSpacing: 0.3,
            }}
          >
            {t("pronos.title")}
          </div>
        </div>

        <div
          className="ns"
          style={{
            display: "flex",
            gap: 6,
            overflowX: "auto",
            padding: "0 14px",
          }}
        >
          <button
            ref={(el) => (pRefs.current["matchs-termines"] = el)}
            onClick={() => {
              setActive("matchs-termines");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            style={{
              flex: "0 0 auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "7px 13px",
              borderRadius: 999,
              fontSize: 12.5,
              fontWeight: 800,
              whiteSpace: "nowrap",
              transition: "all .15s",
              color: showFin ? "#062012" : C.muted,
              background: showFin
                ? `linear-gradient(135deg,#6B6B8A,#4A4A6A)`
                : C.surface,
              border: `1px solid ${showFin ? "transparent" : C.line}`,
            }}
          >
            {t("pronos.finishedCount", totalFin)}
          </button>

          {dates.map((d) => {
            const a = active === d;
            const tod = isToday(d);
            return (
              <button
                key={d}
                ref={(el) => (pRefs.current[d] = el)}
                onClick={() => (showFin ? goDate(d) : scrollTo(d))}
                style={{
                  flex: "0 0 auto",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "7px 13px",
                  borderRadius: 999,
                  fontSize: 12.5,
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  transition: "all .15s",
                  color: a ? "#062012" : C.muted,
                  background: a
                    ? `linear-gradient(135deg,${C.turf},${C.turfDim})`
                    : C.surface,
                  border: `1px solid ${a ? "transparent" : C.line}`,
                }}
              >
                {tod && (
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: a ? "#062012" : C.live,
                    }}
                  />
                )}
                {pillLabel(d, t)}
              </button>
            );
          })}
        </div>
      </div>


      {showFin && (
        <div style={{ padding: "12px 14px 96px" }}>
          {finDates.length === 0 && (
            <div
              style={{
                color: C.faint,
                textAlign: "center",
                padding: "30px 0",
                fontSize: 13,
              }}
            >
              {t("pronos.noFinishedMatches")}
            </div>
          )}
          {finDates.map((d) => (
            <section key={d} style={{ marginBottom: 18 }}>
              <DayLabel date={d} count={finByDate[d].length} />
              {finByDate[d].map((m) => (
                <MatchCard
                  key={m.id}
                  m={m}
                  pick={bets[m.id]}
                  results={results}
                  onBet={onBet}

                  onSetResult={onSetResult}
                  refVals={refScores?.[m.id] || { a: "", b: "" }}
                  onRefScore={(f, v) => onRefScore?.(m.id, f, v)}
                  predVals={predScores?.[m.id] || { a: "", b: "" }}
                  onPredScore={(f, v) => onPredScore?.(m.id, f, v)}
                />
              ))}
            </section>
          ))}
        </div>
      )}

      {!showFin && (
        <div style={{ padding: "12px 14px 96px" }}>
          {dates.map((d) => (
            <section
              key={d}
              data-date={d}
              ref={(el) => (sRefs.current[d] = el)}
              style={{ scrollMarginTop: HDR_OFFSET, marginBottom: 18 }}
            >
              <DayLabel date={d} count={byDate[d].length} />
              {byDate[d].map((m) => (
                <MatchCard
                  key={m.id}
                  m={m}
                  pick={bets[m.id]}
                  results={results}
                  onBet={onBet}

                  onSetResult={onSetResult}
                  refVals={refScores?.[m.id] || { a: "", b: "" }}
                  onRefScore={(f, v) => onRefScore?.(m.id, f, v)}
                  predVals={predScores?.[m.id] || { a: "", b: "" }}
                  onPredScore={(f, v) => onPredScore?.(m.id, f, v)}
                />
              ))}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
